import React, { useState, useEffect, useRef } from "react";
import { IconSearch } from "../assets/icons";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";

const SearchComponent = ({
  onChange,
  searchResults = [],
  onResultClick,
  renderResult,
  isSearchOnEnter = true,
}) => {
  const [query, setQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const blurActiveInput = () => {
    setTimeout(() => window.scrollTo(0, 0), 0);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    onChange?.(value);
    setIsMobileSearchOpen(true);
    setIsDropdownOpen(true);
  };

  const handleSubmitSearch = () => {
    const q = query.trim();
    blurActiveInput();

    setIsDropdownOpen(false);
    setIsMobileSearchOpen(false);

    if (!q) {
      if (location.pathname === "/products") {
        navigate({ pathname: "/products" });
      }
      return;
    }

    const params = createSearchParams({ search: q }).toString();
    navigate({ pathname: "/products", search: `?${params}` });
  };

  const handleDesktopKeyDown = (e) => {
    if (!isSearchOnEnter) return;
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmitSearch();
    }
  };

  const handleMobileKeyDown = (e) => {
    if (!isSearchOnEnter) return;
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmitSearch();
    }
  };

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      setIsMobileSearchOpen(false);
    }
  };

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const q = sp.get("search") ?? "";
    setQuery(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Ngăn cuộn khi giao diện toàn màn hình được mở
  useEffect(() => {
    if (isMobileSearchOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    // Dọn dẹp khi component bị unmount
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isMobileSearchOpen]);

  return (
    <div className="relative">
      {/* Mobile Search Icon */}
      <button
        type="button"
        aria-label="Open search"
        onClick={() => setIsMobileSearchOpen(true)}
        className="lg:hidden flex items-center bg-gray-100 rounded-full p-2"
      >
        <span className="text-gray-500 cursor-pointer">
          <IconSearch className="w-4 h-4" />
        </span>
        <div className="ml-1 text-gray-500 text-xs">Tìm kiếm</div>
      </button>

      {/* Desktop Search Bar */}
      <div
        className={`hidden lg:flex items-center bg-[#F5F5F5] rounded-md px-4 py-2`}
      >
        <span className="text-gray-500">
          <IconSearch />
        </span>
        <input
          type="text"
          placeholder="Tìm kiếm..."
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleDesktopKeyDown}
          className="flex-1 bg-transparent border-none focus:outline-none focus:ring-0 ml-2"
        />
      </div>

      {/* Gợi ý kết quả cho desktop */}
      {isDropdownOpen && searchResults.length > 0 && query && (
        <div className="absolute z-10 bg-white border border-gray-200 rounded-md mt-2 w-full shadow-lg hidden lg:block">
          {searchResults.map((result, index) => (
            <div
              key={index}
              className="flex items-center p-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => onResultClick?.(result)}
            >
              {renderResult ? renderResult(result) : result.name}
            </div>
          ))}
        </div>
      )}

      {/* Mobile Fullscreen Search */}
      {isMobileSearchOpen && (
        <div
          className={`lg:hidden fixed top-[5rem] left-0 right-0 z-50 bg-black/30 flex flex-col h-screen
          transition-all duration-300`}
          style={{ backdropFilter: "blur(5px)" }}
          onClick={handleBackdropClick}
        >
          {/* Header */}
          <div
            className="flex items-center p-4 border-b border-gray-200 bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-gray-500 cursor-pointer mr-4">
              <IconSearch className="w-4 h-4 stroke-black" />
            </span>
            <input
              type="text"
              placeholder="Bạn muốn tìm gì?"
              autoFocus={true}
              value={query}
              onChange={handleInputChange}
              onKeyDown={handleMobileKeyDown}
              className="flex-1 bg-transparent border-none focus:outline-none focus:ring-0"
            />
          </div>

          {/* Gợi ý kết quả cho mobile */}
          {isMobileSearchOpen && query && (
            <div className="h-fit overflow-y-auto bg-white">
              {searchResults.length > 0 && query ? (
                searchResults.map((result, index) => (
                  <div
                    key={index}
                    className="flex items-center p-4 hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      onResultClick?.(result);
                      setIsMobileSearchOpen(false); // Đóng giao diện sau khi chọn
                    }}
                  >
                    {renderResult ? renderResult(result) : result.name}
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 my-4">
                  Không tìm thấy kết quả
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchComponent;
